'use client'

import * as Sentry from '@sentry/nextjs'
import Image from 'next/image'
import Link from 'next/link'

import { Skeleton } from '@/_components/ui/skeleton'
import ZSuspenseError from '@/_components/universal/z-suspense-error'
import { useSectionParams } from '@/_i18n/section-params'
import { nextLinkHrefFromPathWithOptionalHash } from '@/_lib/next-link-href'
import { cn, handleProgramaticScroll } from '@/_lib/utils'
import { IAPCategoryReduced } from '@/_types'
import { useGetAPCategories } from '@/app/[locale]/annual-programs/services/ap-categories.query'

import ZHomeSectionLayout, {
	ZHomeSectionLayoutDescription,
	ZHomeSectionLayoutTitle
} from './_components/z-home-section-layout'

const AHomeAnnualProgramCard: React.FC<{ annualProgramCategory: IAPCategoryReduced }> = ({
	annualProgramCategory
}) => {
	const { getSectionParams } = useSectionParams('annualPrograms')
	const categoryHubLinkHref = nextLinkHrefFromPathWithOptionalHash(
		`/rocne-programy#${getSectionParams('annualProgramCategory')}-${annualProgramCategory.slug}`
	)

	return (
		<article
			className={cn(
				'item_action',
				'aspect-square w-full h-full flex flex-col relative',
				annualProgramCategory.sectionTheme &&
					`item_action_${annualProgramCategory.sectionTheme}`
			)}
			style={{ overflow: 'visible' }}
		>
			<div className='item_action_inner flex flex-col h-full rounded-[22px] overflow-hidden'>
				<Link
					href={categoryHubLinkHref}
					title={annualProgramCategory.name}
					className='item_action_image h-full block'
					prefetch={false}
					onClick={handleProgramaticScroll}
				>
					<Image
						src={
							annualProgramCategory.image?.url ||
							'/assets/layout/default-program.webp'
						}
						alt={`Medante - ${annualProgramCategory.name}`}
						fill
						sizes='100%'
						priority
						quality={100}
						className='object-cover'
						unoptimized={true}
					/>
				</Link>
				<Link
					href={categoryHubLinkHref}
					title={annualProgramCategory.name}
					className={`item_action_bottom cursornodrag flex justify-between items-center py-2.5! min-h-0!`}
					prefetch={false}
					onClick={handleProgramaticScroll}
				>
					<h4 className='item_action_title whitespace-nowrap'>
						<span className='hidden lg:block'>{annualProgramCategory.name}</span>
						<span className='block lg:hidden'>
							{annualProgramCategory.nameShort ?? annualProgramCategory.name}
						</span>
					</h4>
					<svg className='sico'>
						<use href='/assets/layout/icons.svg#s_ar2_r' />
					</svg>
				</Link>
			</div>

			{annualProgramCategory.slug === 'rodina' && (
				<div className='absolute -top-3 lg:-top-5 -right-4 lg:-top-5 lg:-right-5 h-14 w-14 lg:w-18 lg:h-18 bg-[rgb(163,65,164)] flex justify-center items-center text-center z-10 rounded-full'>
					<span className='text-white text-sm'>
						10%
						<br />
						zľava
					</span>
				</div>
			)}
		</article>
	)
}

const AHomeAnnualProgramSkeletonCard = () => (
	<Skeleton className='h-full w-full aspect-square rounded-[22px]' />
)

const AHomeAnnualPrograms = ({ className }: { className?: string }) => {
	const { getSectionParams } = useSectionParams('annualPrograms')
	const { data: annualProgramCategories, isPending, isError, error } = useGetAPCategories()

	const sectionTitle = (
		<ZHomeSectionLayoutTitle
			title='Balíky celoročnej zdravotnej starostlivosti'
			className='max-w-[590px]'
		/>
	)
	const sectionDescription = (
		<ZHomeSectionLayoutDescription description='Ponúkame výhodné ročné programy zdravotnej starostlivosti, ktoré sú prispôsobené potrebám dospelých, detí aj rodín.' />
	)

	if (isPending) {
		return (
			<ZHomeSectionLayout
				title={sectionTitle}
				description={sectionDescription}
				id={getSectionParams('chooseProgramCategory')}
				className={className}
			>
				<div className='grid grid-cols-2 lg:grid-cols-4 gap-3 max-w-[660px] mx-auto lg:max-w-none'>
					{Array.from({ length: 4 }, (_, i) => (
						<AHomeAnnualProgramSkeletonCard key={i} />
					))}
				</div>
			</ZHomeSectionLayout>
		)
	}

	if (isError) {
		if (error) {
			Sentry.captureException(error)
		}

		return <ZSuspenseError title='Nepodarilo sa načítať ročné programy' />
	}

	return (
		<ZHomeSectionLayout
			title={sectionTitle}
			description={sectionDescription}
			id={getSectionParams('chooseProgramCategory')}
			className={className}
		>
			<div className='grid grid-cols-2 lg:grid-cols-4 gap-3 max-w-[660px] mx-auto lg:max-w-none'>
				{(annualProgramCategories ?? []).map(annualProgramCategory => (
					<AHomeAnnualProgramCard
						key={annualProgramCategory.id}
						annualProgramCategory={annualProgramCategory}
					/>
				))}
			</div>
		</ZHomeSectionLayout>
	)
}

export default AHomeAnnualPrograms
